import localforage from 'localforage'
interface Cache<T> {
  date: number
  data: T
}

const DB_NAME = 'fifo_cache'
export const clearAllFIFOCache = async () => {
  if (import.meta.client) {
    await localforage.dropInstance({ name: DB_NAME })
  }
}

export const useFIFOCache = <T>(
  id: string,
  size: number,
  age: number,
  putCondition: (t: T) => {} = null,
) => {
  if (import.meta.client) {
    const store = localforage.createInstance({
      driver: localforage.INDEXEDDB,
      name: DB_NAME,
    })

    const refresh = async () => {
      const now = JstDate.now()
      const forSort: { date: number; key: string }[] = []
      await store.iterate<Cache<T>, void>((value, key) => {
        if (key.startsWith(id)) {
          if (value.date < now - age) {
            store.removeItem(key)
          } else {
            forSort.push({ date: value.date, key })
          }
        }
      })
      if (forSort.length > size) {
        forSort.sort((a, b) => a.date - b.date)
        for (let i = 0; i < forSort.length - size; i++) {
          store.removeItem(forSort[i].key)
        }
      }
    }

    const clear = async () => {
      await store.clear()
    }

    const ikey = (key: string) => {
      return id + '__' + key
    }

    const get = async (key: string): Promise<T> => {
      const cache = await store.getItem<Cache<T>>(ikey(key))
      if (cache) {
        if (cache.date < JstDate.now() - age) {
          return null
        }
        return cache.data
      }
      return null
    }

    const put = async (key: string, value: T) => {
      if (putCondition && !putCondition(value)) {
        return
      }
      await store.setItem<Cache<T>>(ikey(key), {
        data: value,
        date: JstDate.now(),
      })
      refresh()
    }

    const remove = async (key: string) => {
      await store.removeItem(ikey(key))
    }

    const getOrPut = async (
      key: string,
      getter: () => Promise<T>,
    ): Promise<T> => {
      const cachedData = await get(key)
      if (cachedData) {
        return cachedData
      }
      const value = await getter()
      put(key, value)
      return value
    }

    return { get, put, remove, clear, getOrPut }
  } else {
    const clear = () => {}

    const get = (key: string): Promise<T> => {
      return null
    }

    const put = (key: string, value: T) => {}

    const remove = (key: string) => {}

    const getOrPut = async (
      key: string,
      getter: () => Promise<T>,
    ): Promise<T> => {
      return await getter()
    }

    return { get, put, remove, clear, getOrPut }
  }
}
